import { UserRound } from "lucide-react";
import { type CSSProperties, type ReactElement } from "react";

import { cn } from "../utils/cn";

export interface AvatarProps {
  initials: string;
  size?: number;
  /** Custom background color (e.g. for colored admin avatars). Defaults to primary-100. */
  color?: string;
  className?: string;
  alt?: string;
  /** "placeholder" (Anonym-Ansicht, #140): a neutral person icon, no
   * initials and no label, so nothing hints at who it is. */
  variant?: "initials" | "placeholder";
}

/** Initials avatar — round, primary-tinted. Size is a raw px value so the
 * prototype's scale (24/32/44/64/80) can be reproduced 1:1. */
export function Avatar({
  initials,
  size = 32,
  color,
  className,
  alt,
  variant = "initials",
}: AvatarProps): ReactElement {
  if (variant === "placeholder") {
    return (
      <span
        aria-hidden="true"
        style={{ width: size, height: size }}
        className={cn(
          "inline-flex shrink-0 items-center justify-center rounded-full bg-neutral-100 text-neutral-500",
          className,
        )}
      >
        <UserRound size={Math.round(size * 0.5)} />
      </span>
    );
  }
  const style: CSSProperties = { width: size, height: size, fontSize: Math.round(size * 0.42) };
  if (color) {
    style.background = color;
    style.color = "#fff";
  }
  return (
    <span
      role="img"
      aria-label={alt ?? initials}
      style={style}
      className={cn(
        "inline-flex shrink-0 items-center justify-center rounded-full font-semibold tracking-tight leading-none",
        !color && "bg-primary-100 text-primary-700",
        className,
      )}
    >
      {initials}
    </span>
  );
}
